import React, {useEffect, useState} from "react";

import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";

import {formatCurrency, formatDecimal, getHostName, getKeyValue} from "../../utils/Utils";
import {toDateTimeString} from "../../utils/TimeUtil";

import {AppContext} from "../../HomePage";
import {IndexTemplate} from "../../utils/InlineComponents";
import ProductSelectionBar from "./ProductSelectionBar";
import {ProductService} from "../../service/ProductService";
import SiteSearchSummary from "./SiteSearchSummary";

const SOFT = {
    green: {bg: '#dcfce7', fg: '#15803d'},
    red:   {bg: '#fee2e2', fg: '#b91c1c'},
};

const TAG_STYLE = {padding: '.15em .5em', borderRadius: '6px', fontSize: '.8rem', display: 'inline-block'};

// Parent-product code fields used as highlight sources, same set as the
// seller-suggestion screen (ref_id is parent-only but still worth looking for).
const PRODUCT_CODE_FIELDS = ['sku', 'mpn', 'ean', 'upc', 'asin', 'barcode', 'ref_id'];
// Codes shorter than this are skipped so a stray 1-2 char value doesn't light up
// unrelated text. Real product identifiers are essentially always longer.
const MIN_CODE_LENGTH = 3;

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Mirrors CompetitorSuggestion.js's layout and interaction model for the
// supplier dataset. Deliberately narrower than CompetitorSuggestion: match
// scoring (MatchScoreService), the used_base_spider / source_campaign_name
// provenance and the cleanup revert/restore flow are all competitor-only
// subsystems that never write to supplier_suggestions, so their controls are
// dropped here rather than shipped empty. See task-18-report.md for the
// field-by-field evidence.
const SupplierSuggestion = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const productService = new ProductService();

    const codeHighlightRegex = React.useMemo(() => {
        const product = props.selectedProduct || {};
        const seen = new Set();
        const codes = [];
        PRODUCT_CODE_FIELDS.forEach(field => {
            const value = product[field];
            if (value == null) return;
            const code = String(value).trim();
            if (code.length < MIN_CODE_LENGTH) return;
            const key = code.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            codes.push(code);
        });
        if (codes.length === 0) return null;
        codes.sort((a, b) => b.length - a.length);
        return new RegExp('(' + codes.map(escapeRegExp).join('|') + ')', 'gi');
    }, [props.selectedProduct]);

    const highlightCodes = (text) => {
        if (text == null) return text;
        const str = String(text);
        if (!codeHighlightRegex) return str;
        const parts = str.split(codeHighlightRegex);
        if (parts.length === 1) return str;
        return parts.map((part, i) =>
            i % 2 === 1 ? <mark key={i} className="pux-code-hl">{part}</mark> : part);
    };

    const [filters, setFilters] = useState({is_matched: null, name: ""});
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSuggestions, setSelectedSuggestions] = useState([]);
    const [suggestionsBusy, setSuggestionsBusy] = useState(false);

    const matchedOptions = [
        {name: t('yes_no.all'), value: "all"},
        {name: t('actions.decision_not_made'), value: null},
        {name: t('actions.supplier_added'), value: true, className: "green"},
        {name: t('actions.supplier_ignored'), value: false, className: "yellow"}
    ];

    useEffect(() => {
        const suggestions = props.selectedProduct.supplier_suggestions ? props.selectedProduct.supplier_suggestions.filter(suggestion =>
            ((!filters.name || (suggestion.name && suggestion.name.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.product_url && getHostName(suggestion.product_url).toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.sku && suggestion.sku.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.ean && suggestion.ean.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.mpn && suggestion.mpn.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.upc && suggestion.upc.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.barcode && suggestion.barcode.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.asin && suggestion.asin.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.price && suggestion.price.value && suggestion.price.value.toString().toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.price && suggestion.price.value && formatDecimal(suggestion.price.value).toString().toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (suggestion.price && suggestion.price.currency && suggestion.price.currency.toLowerCase().includes(filters.name.toLowerCase())))) &&
            (filters.is_matched === "all" ||
                (filters.is_matched === null && !suggestion.is_match_decision_made) ||
                (filters.is_matched === true && suggestion.is_match_decision_made && suggestion.is_matched) ||
                (filters.is_matched === false && suggestion.is_match_decision_made && !suggestion.is_matched)
            )) : [];
        setSuggestions([...suggestions]);
    }, [props.selectedProduct, filters]);

    const onChange = (e) => {
        const {key, value} = getKeyValue(e);
        setFilters({...filters, [key]: value});
    };

    const setFilter = (key, value) => setFilters({...filters, [key]: value});
    const chipColor = (opt) => opt.className === 'green' ? 'pux-chip-green'
        : (opt.className === 'yellow' ? 'pux-chip-amber' : 'pux-chip-blue');
    const filterChip = (opt, key) => (
        <button key={key + '-' + String(opt.value)} type="button"
                className={"pux-chip pux-chip-toggle " + (filters[key] === opt.value ? chipColor(opt) : 'off')}
                onClick={() => setFilter(key, opt.value)}>{opt.name}</button>
    );

    const approveSuggestions = (rows) => {
        const pending = rows.filter(row => !row.is_match_decision_made);
        if (pending.length === 0 || suggestionsBusy) return;
        const suppliers = pending.map(row => ({product_url: row.product_url}));
        setSuggestionsBusy(true);
        productService.addSupplierFromSuggestions(props.selectedProduct.id, suppliers).then(() => {
            setSelectedSuggestions([]);
            if (props.refreshProducts) props.refreshProducts();
            toast.current.show({severity: "success", summary: t("supplier.suggestion_approved"), detail: t("message_detail.successful")});
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setSuggestionsBusy(false));
    };

    const rejectSuggestions = (rows) => {
        const pending = rows.filter(row => !row.is_match_decision_made);
        if (pending.length === 0 || suggestionsBusy) return;
        const supplierSuggestions = pending.map(row => ({product_url: row.product_url}));
        setSuggestionsBusy(true);
        productService.ignoreSupplierSuggestion(props.selectedProduct.id, supplierSuggestions).then(() => {
            setSelectedSuggestions([]);
            if (props.refreshProducts) props.refreshProducts();
            toast.current.show({severity: "success", summary: t("supplier.suggestion_rejected"), detail: t("message_detail.successful")});
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setSuggestionsBusy(false));
    };

    const approveAllSuggestions = () => approveSuggestions(selectedSuggestions.length > 0 ? selectedSuggestions : suggestions);
    const rejectAllSuggestions = () => rejectSuggestions(selectedSuggestions.length > 0 ? selectedSuggestions : suggestions);

    const suggestionImageTemplate = (rowData) => {
        return <div>
            <span className="p-column-title">{t('product.image')}</span>
            <img referrerPolicy='no-referrer' className="img-fluid-thumb" src={rowData.image} alt=""/>
        </div>;
    };

    const suggestionNameTemplate = (rowData) => {
        return <div>
            <span className="p-column-title">{t('table_headers.name')}</span>
            {rowData.product_url &&
                <div style={{marginBottom: '.25em'}}>
                    <a href={rowData.product_url} target="_blank" rel="noopener noreferrer">
                        {getHostName(rowData.product_url)}
                    </a>
                    {rowData.is_match_decision_made && rowData.is_matched &&
                        <span className="p-tag" style={{...TAG_STYLE, marginLeft: '.5em', background: SOFT.green.bg, color: SOFT.green.fg}}>{t('suggestion.status_matched')}</span>}
                    {rowData.is_match_decision_made && !rowData.is_matched &&
                        <span className="p-tag" style={{...TAG_STYLE, marginLeft: '.5em', background: '#f3f4f6', color: '#6b7280'}}>{t('suggestion.status_unmatched')}</span>}
                </div>}
            {highlightCodes(rowData.name)}
            {rowData.brand && <div><b>{t('product.brand')} : </b>{rowData.brand}</div>}
            {rowData.category && <div><b>{t('product.category')} : </b>{rowData.category}</div>}
            {rowData.sku && <div><b>{t('product.sku')} : </b>{highlightCodes(rowData.sku)}</div>}
            {rowData.mpn && <div><b>{t('product.mpn')} : </b>{highlightCodes(rowData.mpn)}</div>}
            {rowData.ean && <div><b>{t('product.ean')} : </b>{highlightCodes(rowData.ean)}</div>}
            {rowData.upc && <div><b>{t('product.upc')} : </b>{highlightCodes(rowData.upc)}</div>}
            {rowData.asin && <div><b>{t('product.asin')} : </b>{highlightCodes(rowData.asin)}</div>}
            {rowData.barcode && <div><b>{t('product.barcode')} : </b>{highlightCodes(rowData.barcode)}</div>}
            {rowData.price && rowData.price.created_at &&
                <div style={{color: '#6b7280', fontSize: '.8rem', marginTop: '.25em'}}>
                    {t('suggestion.suggested_at')}: {toDateTimeString(rowData.price.created_at)}
                </div>}
        </div>;
    };

    const suggestionPriceTemplate = (rowData) => <div className="p-text-nowrap p-text-right">
        <span className="p-column-title">{t('product_detail.price')}</span>
        {rowData.price && rowData.price.value ? (formatCurrency(rowData.price.value, rowData.price.currency)) : ""}
        <div>
            {rowData.price && rowData.price.stock > 0
                ? <span className="p-tag" style={{...TAG_STYLE, background: SOFT.green.bg, color: SOFT.green.fg, border: 'none'}}>{t('product.stock_in')}</span>
                : <span className="p-tag" style={{...TAG_STYLE, background: SOFT.red.bg, color: SOFT.red.fg, border: 'none'}}>{t('product.stock_out')}</span>}
        </div>
    </div>;

    const suggestionActionTemplate = (rowData) => {
        return <div className="p-text-right" style={{whiteSpace: 'normal'}}>
            <span className="p-column-title">{t('table_headers.action')}</span>
            <Button icon="pi pi-check-circle" className="p-button-success p-button-sm" disabled={suggestionsBusy}
                    tooltip={t('supplier.approve_suggestion')} tooltipOptions={{position: 'bottom'}}
                    style={{marginRight: '.5em', marginBottom: '.25em', background: SOFT.green.bg, borderColor: SOFT.green.bg, color: SOFT.green.fg}}
                    onClick={(e) => { e.stopPropagation(); approveSuggestions([rowData]); }}/>
            <Button icon="pi pi-times-circle" className="p-button-warning p-button-sm" disabled={suggestionsBusy}
                    tooltip={t('supplier.reject_suggestion')} tooltipOptions={{position: 'bottom'}}
                    style={{marginBottom: '.25em'}}
                    onClick={(e) => { e.stopPropagation(); rejectSuggestions([rowData]); }}/>
        </div>;
    };

    const decisionColor = (rowData) => {
        if (selectedSuggestions.some(s => s.product_url === rowData.product_url))
            return {'pux-row-selected': true};
        return "";
    };

    return (
        <React.Fragment>
            <SiteSearchSummary productId={props.selectedProduct.id} target="supplier"
                               hasSuggestions={(props.selectedProduct.supplier_suggestions || [])
                                   .some(suggestion => !suggestion.is_match_decision_made)}/>
            <div className="pux-fchips">
                <span className="p-input-icon-left">
                    <i className="pi pi-search"/>
                    <InputText id="name" name="name" value={filters.name} onChange={onChange}
                               placeholder={t('product.globalFilter')} style={{width: '14em'}}/>
                </span>
                <span className="pux-fchips-sep"/>
                {matchedOptions.map(opt => filterChip(opt, 'is_matched'))}
            </div>

            <ProductSelectionBar
                t={t}
                count={selectedSuggestions.length}
                primaryActions={[
                    {key: 'approve', label: t('supplier.approve_suggestion'), icon: 'pi pi-check-circle',
                        className: 'p-button-success', command: approveAllSuggestions},
                    {key: 'reject', label: t('supplier.reject_suggestion'), icon: 'pi pi-times-circle',
                        className: 'p-button-warning', command: rejectAllSuggestions},
                ]}
                onClear={() => setSelectedSuggestions([])}
            />

            <DataTable value={suggestions}
                       responsive={true}
                       resizableColumns={true} columnResizeMode="fit"
                       rowClassName={decisionColor}
                       dataKey="product_url"
                       selectionMode="multiple" metaKeySelection={false}
                       selection={selectedSuggestions} onSelectionChange={(e) => setSelectedSuggestions(e.value)}>
                <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                <Column field="image" body={suggestionImageTemplate} header={t('product.image')} headerStyle={{width: '12%'}}/>
                <Column field="name" body={suggestionNameTemplate} header={t('table_headers.name')} headerStyle={{width: '54%'}}/>
                <Column field="price" body={suggestionPriceTemplate} header={t('product_detail.price')} headerStyle={{textAlign: "right", width: '14%'}}/>
                <Column body={suggestionActionTemplate} header={t('table_headers.action')} headerStyle={{textAlign: 'right', width: '170px'}} className="preventSelect"/>
            </DataTable>
        </React.Fragment>
    );
};
export default SupplierSuggestion;
